if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 768px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.setActiveKeyFeature(thumbnail.dataset.mediaSku);
      }

      setActiveMedia(mediaId, prepend) {
        if (this.elements.viewer.length > 0) {
          const removedViewerMedia = this.elements.viewer.shift();
          if (this.elements.thumbnails) {
            // Find and remove corresponding thumbnail
            const removedThumbnail = this.mediaThumbnails.find(thumbnail =>
              thumbnail.dataset.target === removedViewerMedia.dataset.mediaId
            );
            const index = this.mediaThumbnails.indexOf(removedThumbnail);
            if (index !== -1) {
              this.mediaThumbnails.splice(index, 1);
            }
          }
        }

        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is--active');
        });
        activeMedia.classList.add('is--active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveMediaForVariant(mediaSku, productId, featuredMediaId) {
        // Media modification for Breathable, Organic Cotton Sheets (2-pack) & Breathable Organic Fitted Mini Crib Sheets
        const thumbsDomAltercation = [4314407665722, 8103449395397]
        if (this.elements.viewer.length > 0) {
          const removedViewerMedia = this.elements.viewer.shift();
          if (this.elements.thumbnails) {
            // Find and remove corresponding thumbnail
            const removedThumbnail = this.mediaThumbnails.find(thumbnail =>
              thumbnail.dataset.target === removedViewerMedia.dataset.mediaSku
            );
            const index = this.mediaThumbnails.indexOf(removedThumbnail);
            if (index !== -1) {
              this.mediaThumbnails.splice(index, 1);
            }
          }
        }

        let activeMedia = this.elements.viewer.querySelector(`[data-media-sku*="${mediaSku}"]`);
        // Fallback: no image alt contains the variant SKU — use the variant's assigned featured media id
        if (activeMedia === null && featuredMediaId) {
          activeMedia = this.elements.viewer.querySelector(`[data-media-id$="-${featuredMediaId}"]`);
        }
        if ( activeMedia === null ) {
          console.log('[Gallery SKU match] No match found for:', mediaSku, '| Available SKUs:', Array.from(this.elements.viewer.querySelectorAll('[data-media-sku]')).map(el => el.dataset.mediaSku));
          return;
        }

        this.elements.viewer.querySelectorAll('[data-media-sku]').forEach(element => {
          element.classList.remove('is--active');
        });
        activeMedia.classList.add('is--active');

        this.preventStickyHeader();
        if (this.elements.thumbnails) {
          const originalScrollBehavior = activeMedia.parentElement.style.scrollBehavior;
          activeMedia.parentElement.style.scrollBehavior = 'auto';
          activeMedia.parentElement.scrollLeft = activeMedia.offsetLeft;
          activeMedia.parentElement.style.scrollBehavior = originalScrollBehavior;
        }
        this.playActiveMedia(activeMedia);

        // Hide thumbnails that don't match the media SKU
        if (thumbsDomAltercation.includes(productId))
          this.elements.thumbnails.querySelectorAll('[data-media-sku]').forEach(function(thumbnail) {
            const thumbSku = thumbnail.getAttribute('data-media-sku');

            if (!thumbSku.includes(mediaSku) && !thumbSku.includes('all')) {
              thumbnail.classList.add('hidden');
            } else {
              thumbnail.classList.remove('hidden');
            }
          });

        if (!this.elements.thumbnails) return;
        let activeThumbnail = this.elements.thumbnails.querySelector(`[data-media-sku*="${mediaSku}"]`);
        // Fallback: thumbnails key off data-target ("section-mediaId"), so match the featured media id there too
        if (activeThumbnail === null && featuredMediaId) {
          activeThumbnail = this.elements.thumbnails.querySelector(`[data-target$="-${featuredMediaId}"]`);
        }
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      setActiveKeyFeature(keyFeature) {
        const keyFeaturesControls = this.querySelector('key-features-media-controls');
        if (!keyFeaturesControls) return;

        keyFeaturesControls.querySelectorAll('[control-item]').forEach(item => {
          item.classList.remove('border-green-light');
          item.classList.add('border-gray-light');
            const controlMedia = item.getAttribute('control-item');
            if (controlMedia && keyFeature.includes(controlMedia)) {
              item.classList.remove('border-gray-light');
              item.classList.add('border-green-light');
            }
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
